"use client";

import {
  runArtmatchEmbedInBackground,
  shouldTriggerArtmatchEmbed,
  useEmbedAttachment,
} from "@/api/artmatch";
import {
  Attachment,
  attachmentSchema,
  buildAttachmentUpdatePayload,
  useCreateGenericImageAttachment,
  useDeleteAttachment,
  useDeleteHdAttachment,
  useGetAttachmentsByDocId,
  useReorderAttachments,
  useUpdateAttachment,
} from "@/api/attachment";
import {
  buildAttachmentMetadataOverrides,
  hydrateAttachmentMultilingualFields,
} from "@/lib/media/attachment-multilingual";
import { useGetForfait } from "@/api/forfait";
import { uploadImage, uploadVideo } from "@/api/image.ts";
import { isVideoFile } from "@/lib/media/video";
import { resolveEmbedThumbnailUrl } from "@/lib/media/embed";
import {
  mediaLibraryTranslationKey,
  mediaLibraryFormTranslationKey,
  resolveMediaLibraryKind,
} from "@/lib/media/media-library-labels";
import { useGetGenericImageSettings } from "@/api/user-settings";
import { EmbedPlayer } from "@/components/media/EmbedPlayer";
import { ResponsiveVideo } from "@/components/media/ResponsiveVideo";
import StyledDrawer from "@/components/StyledDrawer.tsx";
import { Button } from "@/components/ui/button.tsx";
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form.tsx";
import { Input } from "@/components/ui/input.tsx";
import { Switch } from "@/components/ui/switch.tsx";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabsContained.tsx";
import { useAuth } from "@/context/auth/AuthContext";
import { toast } from "@/hooks/use-toast";
import {
  attachmentsToGalleryImages,
  CopyFromMasterConfig,
  galleryIndexForAttachment,
  isMainPosition,
  optionsPosition,
  TypeOptionPosition,
} from "@/lib/image";
import { ImportFromMasterDrawers } from "./ImportFromMasterDrawers";
import AddEmbedForm from "./AddEmbedForm";
import { ImageStudioHost } from "./ImageStudioHost";
import { canRetouchAttachment } from "./can-retouch-attachment";
import { Forfait, Attachment as UploadAttachmentPayload } from "@/lib/types";
import { arrayMove } from "@dnd-kit/sortable";
import { zodResolver } from "@hookform/resolvers/zod";
import { Cross1Icon, Cross2Icon, PlusIcon } from "@radix-ui/react-icons";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { ColumnDef } from "@tanstack/react-table";
import { isAxiosError } from "axios";
import {
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarTrigger,
} from "@/components/ui/menubar";
import {
  CheckCircleIcon,
  ChevronLeft,
  ChevronDownIcon,
  Copyright,
  Crown,
  Edit2,
  Film,
  GripVertical,
  ImageIcon,
  LayoutGrid,
  Aperture,
  Play,
  Plus,
  Table2,
  Trash2,
  XCircleIcon,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
} from "react";
import { FormProvider, useForm, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import { z } from "zod";
import DragAndDropLoadMultipleImages from "../form-fields/DragAndDropLoadMultipleImages";
import { BatchUploadProgressPanel } from "./BatchUploadProgressPanel";
import { extractUploadErrorMessage, formatUploadErrorForDisplay } from "./batchUploadErrors";
import {
  type BatchFileUploadStatus,
  type BatchUploadProgress,
  getBatchUploadDisplayCurrent,
} from "./batchUploadTypes";
import { useBatchUploadProgressToast } from "./useBatchUploadProgressToast";
import HdOption from "../hd/HdOption";
import ShowOptionsDialog from "../hd/ShowOptionsDialog";
import { UniqueImageFullScreen } from "../modal/UniqueImageFullScreen";
import { ConfirmActionDialog } from "../ui/confirm-action-dialog";
import { DataTable } from "../ui/datatable";
import { Dialog, DialogClose, DialogContent, DialogTitle } from "../ui/dialog";
import { FileItemDisplay } from "../ui/file-item-display";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Separator } from "../ui/separator";
import { Sortable, SortableDragHandle, SortableItem } from "../ui/sortable";
import {
  GenericImagePreview,
  hasGenericImageConfigured,
} from "./GenericImagePreview";

import { getMainLanguage, supportedLocales, SupportedLocale } from "@/i18n";
import {
  cn,
  isEmptyRichText,
  sortFilesByNameNatural,
  stripExtension,
} from "@/lib/utils";
import { Col } from "../form-items/AbstractForm";
import { ModifiableList } from "../form-items/ModifiableList";
import { MultilingualField } from "../form-items/MultilingualField";
import Tiptap from "../tiptap/TipTap";
import { Checkbox } from "../ui/checkbox";

function normalizeAttachmentNotes(
  notes?: Record<string, string | undefined> | null,
): Record<string, string> | null {
  if (notes == null) return null;
  const cleaned: Record<string, string> = {};
  for (const [key, raw] of Object.entries(notes)) {
    if (!supportedLocales.includes(key as SupportedLocale)) continue;
    if (typeof raw !== "string" || isEmptyRichText(raw)) continue;
    cleaned[key] = raw;
  }
  return Object.keys(cleaned).length > 0 ? cleaned : null;
}

const optionalAttachmentNotesSchema = z.preprocess(
  (val) => (val === null ? undefined : val),
  z.record(z.enum(supportedLocales), z.string().optional()).optional(),
);

const imageSchema = attachmentSchema
  .pick({
    name: true,
    textAlt: true,
    caption: true,
    position: true,
    published: true,
    isHd: true,
    photoCredits: true,
    videoLoop: true,
  })
  .extend({
    notes: optionalAttachmentNotesSchema,
    image: z.instanceof(File).optional(),
    images: z.array(z.instanceof(File)).optional().default([]),
    position: z.string().min(1, "attachment.form.position.required"),
    applyWatermark: z.boolean().optional().default(true),
    isGenericImage: z.boolean().optional().default(false),
  });

export type ImageFormData = z.infer<typeof imageSchema>;

interface UploadOrEditPictureProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  selectedImage: Attachment | null;
  setSelectedImage: (image: Attachment | null) => void;
  onSubmit: (data: ImageFormData) => Promise<void>;
  onDelete?: (id: string) => void;
  type: TypeOptionPosition;
  form: UseFormReturn<ImageFormData>;
  inputName?: boolean;
  disabled?: boolean;
  /** When true, show the "Apply watermark on this image" switch (only for upload). */
  watermarkModuleEnabled?: boolean;
  /** All attachments for the document; used to enforce a single "Face" (main) position. */
  attachments?: Attachment[];
  /** When true, the drawer is controlled externally (no trigger button). */
  hideTrigger?: boolean;
  uploadProgress?: BatchUploadProgress | null;
  batchUploadActive?: boolean;
  onDrawerOpenChange?: (open: boolean) => void;
  /** When true, widen the upload/edit drawer for nested drawer contexts. */
  embeddedInDrawer?: boolean;
  /**
   * Simplified drawer: keeps only the dropzone, Nom, Position and publication
   * status; hides the advanced fields (HD, generic image, caption/alt,
   * watermark, video loop, credits, notes). Used by the simplified sheet view.
   */
  simplified?: boolean;
  onRetouch?: (attachment: Attachment) => void;
}

const DRAWER_PORTRAIT_VIDEO_MAX_HEIGHT = "85dvh";

function getAttachmentPreviewUrl(attachment: Attachment): string | undefined {
  if (attachment.kind === "video") {
    return (
      attachment.posterPublicUrl ??
      attachment.mediumPublicUrl ??
      attachment.publicUrl ??
      undefined
    );
  }
  return attachment.mediumPublicUrl ?? attachment.publicUrl ?? undefined;
}

export const UploadOrEditPicture = ({
  open,
  setOpen,
  selectedImage,
  setSelectedImage,
  onSubmit,
  onDelete,
  type,
  form,
  inputName,
  disabled,
  watermarkModuleEnabled,
  attachments,
  hideTrigger = false,
  uploadProgress = null,
  batchUploadActive = false,
  onDrawerOpenChange,
  embeddedInDrawer = false,
  simplified = false,
  onRetouch,
}: UploadOrEditPictureProps) => {
  const { t } = useTranslation();
  const vt = useVisibleT();
  const { data: forfait } = useGetForfait();
  const { data: genericImageSettings } = useGetGenericImageSettings();
  const { handleSubmit, control } = form;
  const [hdOptionSelected, setHdOptionSelected] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);

  const isGenericImage = form.watch("isGenericImage") === true;
  const selectedImages: File[] = form.watch("images") ?? [];
  const selectedImageCount = selectedImages.length;
  const isVideoMediaContext = useMemo(() => {
    if (selectedImage?.kind === "video" || selectedImage?.kind === "embed") {
      return true;
    }
    if (selectedImage) return false;
    if (selectedImages.length === 0) return false;
    return selectedImages.every((file) => isVideoFile(file));
  }, [selectedImage, selectedImages]);
  const drawerMediaKind = useMemo(
    () =>
      resolveMediaLibraryKind({
        attachment: selectedImage,
        files: selectedImages,
      }),
    [selectedImage, selectedImages],
  );

  const fieldLabel = (label: Parameters<typeof mediaLibraryFormTranslationKey>[0]) =>
    t(mediaLibraryFormTranslationKey(label, drawerMediaKind));

  const usePluralPublicationLabels =
    !selectedImage && selectedImageCount > 1;
  const positionValue = form.watch("position");
  const mainPositionMultiBlocked =
    !selectedImage &&
    isMainPosition(positionValue) &&
    selectedImageCount > 1;
  const genericImageConfigured =
    hasGenericImageConfigured(genericImageSettings);

  const handleSheetOpenChange = (nextOpen: boolean) => {
    if (onDrawerOpenChange) {
      onDrawerOpenChange(nextOpen);
      return;
    }
    if (!nextOpen) {
      form.reset({
        name: "",
        textAlt: {},
        caption: {},
        notes: {},
        photoCredits: "",
        position: "",
        published: false,
        applyWatermark: true,
        isGenericImage: false,
        videoLoop: false,
        images: [],
      });
      setSelectedImage(null);
    }
    setOpen(nextOpen);
  };

  useEffect(() => {
    setHdOptionSelected(selectedImage ? (selectedImage.isHd ?? true) : true);
  }, [selectedImage]);

  useEffect(() => {
    if (selectedImageCount > 0 && form.getValues("isGenericImage")) {
      form.setValue("isGenericImage", false);
    }
  }, [selectedImageCount, form]);

  useEffect(() => {
    if (selectedImage) return;
    if (selectedImageCount !== 1) return;
    const picked = selectedImages[0];
    if (!picked || !isVideoFile(picked)) return;

    form.setValue("name", stripExtension(picked.name), {
      shouldValidate: true,
      shouldDirty: true,
    });
  }, [selectedImage, selectedImageCount, selectedImages, form]);

  // Show loop option when editing/uploading video or embed media.
  const showVideoLoopOption =
    isVideoMediaContext ||
    selectedImage?.kind === "video" ||
    selectedImage?.kind === "embed";

  const showDrawerTrash = Boolean(selectedImage) || selectedImageCount > 0;

  const handleDrawerTrashClick = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (selectedImage && onDelete != undefined) {
      setOpenConfirmationDelete(true);
      return;
    }
    if (selectedImageCount > 0) {
      form.setValue("images", []);
    }
  };

  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [showWarningDialog, setShowWarningDialog] = useState<boolean>(false);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [openConfirmationDelete, setOpenConfirmationDelete] = useState(false);
  const [openFacePositionWarning, setOpenFacePositionWarning] =
    useState<boolean>(false);
  const [selectedOptionName, setSelectedOptionName] = useState<string>("");

  /** True if another attachment (not the current one) already has the given main position (e.g. Face). */
  const isPositionAlreadyAssignedToOther = (position: string) =>
    isMainPosition(position) &&
    (attachments ?? []).some(
      (a) => a.id !== selectedImage?.id && a.position === position,
    );
  const hasHdOption = useMemo(
    () =>
      forfait?.subscribedOptions?.find((option) => option.type === "HD") !==
      undefined,
    [forfait],
  );

  form.watch(["published"]);
  const videoLoopEnabled = form.watch("videoLoop");

  function onFacePositionTriggered() {
    form.setValue("published", true);
  }

  function subscribeToOptionSuccess(name: string) {
    setSelectedOptionName(name);
    setOpenConfirmation(true);
  }

  return (
    <StyledDrawer
      className="min-w-[500px]"
      maxWidth={embeddedInDrawer ? "sm:max-w-2xl" : undefined}
      title={
        selectedImage
          ? t(mediaLibraryTranslationKey("titleEdit", drawerMediaKind))
          : t(mediaLibraryTranslationKey("title", drawerMediaKind))
      }
      sheetProps={{
        open,
        onOpenChange: handleSheetOpenChange,
      }}
      trigger={
        hideTrigger ? undefined : (
          <Button
            className="font-semibold flex gap-2"
            onClick={() => {
              handleSheetOpenChange(!open);
            }}
            disabled={disabled}
          >
            <PlusIcon className="h-4 w-4 relative" strokeWidth={2.5} />
            {t(mediaLibraryTranslationKey("button", "image"))}
          </Button>
        )
      }
      closeButton={
        showDrawerTrash ? (
          <Button
            variant="outline"
            onClick={handleDrawerTrashClick}
            disabled={disabled}
          >
            <Trash2 className="w-5 h-5 text-[#DC2626]" />
          </Button>
        ) : (
          <Cross1Icon
            className="h-5 w-5 relative"
            onClick={() => {
              form.reset();
              setHdOptionSelected(true);
            }}
          />
        )
      }
      arrowClose={showDrawerTrash}
    >
      <FormProvider {...form}>
        <form
          onSubmit={handleSubmit(
            (data) => {
              if (
                !selectedImage &&
                !data.isGenericImage &&
                (!data.images || data.images.length === 0)
              ) {
                form.setError("images", {
                  message: t("attachment.form.image.required"),
                });
                return;
              }
              if (
                !isVideoMediaContext &&
                !data.isGenericImage &&
                !selectedImage?.isGenericImage &&
                ((!hdOptionSelected && !selectedImage) ||
                  (!hdOptionSelected && selectedImage && selectedImage.isHd))
              ) {
                setShowWarningDialog(true);
                return;
              }
              data.isHd =
                data.isGenericImage ||
                selectedImage?.isGenericImage ||
                isVideoMediaContext
                  ? false
                  : hdOptionSelected && hasHdOption;
              setLoading(true);
              void onSubmit(data).finally(() => {
                setLoading(false);
              });
            },
            (errors) => {
              console.log(errors);
              setLoading(false);
            },
          )}
          className="w-full flex flex-col gap-4"
        >
          <div>
            {selectedImage && selectedImage.isGenericImage && (
              <GenericImagePreview
                settings={genericImageSettings}
                className="w-full h-[300px]"
                emptyLabel={t("genericImage.preview.emptyState")}
              />
            )}
            {selectedImage && !selectedImage.isGenericImage && (
              <>
                {selectedImage.kind === "embed" ? (
                  <EmbedPlayer
                    provider={selectedImage.embedProvider}
                    videoId={selectedImage.embedVideoId}
                    embedUrl={selectedImage.embedUrl}
                    videoLoop={videoLoopEnabled}
                    portraitMaxHeight={DRAWER_PORTRAIT_VIDEO_MAX_HEIGHT}
                    className="w-full"
                    title={selectedImage.name}
                  />
                ) : selectedImage.kind === "video" &&
                  selectedImage.videoPublicUrl ? (
                  <ResponsiveVideo
                    src={selectedImage.videoPublicUrl}
                    poster={selectedImage.posterPublicUrl ?? undefined}
                    loop={Boolean(videoLoopEnabled)}
                    portraitMaxHeight={DRAWER_PORTRAIT_VIDEO_MAX_HEIGHT}
                  />
                ) : selectedImage.kind === "video" &&
                  !selectedImage.videoPublicUrl ? (
                  <div className="flex w-full flex-col items-center justify-center gap-2 rounded-md border border-dashed p-6 text-muted-foreground min-h-[180px]">
                    <Film className="h-8 w-8" />
                    <p className="text-center text-sm">
                      {selectedImage.videoStatus === "error"
                        ? t("mediaLibrary.video.conversionError")
                        : t("mediaLibrary.video.processing")}
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-col gap-2">
                  <img
                    src={selectedImage.mediumPublicUrl}
                    alt="Selected"
                    className="w-full max-h-[360px] object-contain"
                  />
                  {onRetouch && canRetouchAttachment(selectedImage) && (
                    <Button
                      type="button"
                      variant="outline"
                      className="self-start"
                      onClick={() => onRetouch(selectedImage)}
                      disabled={disabled}
                    >
                      <Aperture className="mr-2 h-4 w-4" />
                      {vt("imageStudio.retouch", "Retoucher")}
                    </Button>
                  )}
                  </div>
                )}
              </>
            )}
            {!selectedImage && isGenericImage && (
              <div className="flex flex-col gap-2">
                <GenericImagePreview
                  settings={genericImageSettings}
                  className="w-full h-[300px] border border-solid"
                  emptyLabel={t("genericImage.preview.emptyState")}
                />
              </div>
            )}
            {batchUploadActive && uploadProgress ? (
              <BatchUploadProgressPanel progress={uploadProgress} />
            ) : (
              !selectedImage &&
              !isGenericImage && (
                <div className="flex flex-col gap-2">
                  <DragAndDropLoadMultipleImages
                    control={control}
                    config={{ name: "images", type: "inputFile" }}
                    className="bg-brand-200/50 border border-solid z-5 relative"
                    getValues={form.getValues}
                  />
                </div>
              )
            )}
            {selectedImage && !selectedImage.isGenericImage && (
              <FileItemDisplay file={selectedImage} className="z-5 relative" />
            )}
            {!simplified &&
              !isGenericImage &&
              !selectedImage?.isGenericImage &&
              !isVideoMediaContext &&
              (!selectedImage || selectedImage?.isHd) && (
                <HdOption
                  disabled={
                    batchUploadActive ||
                    (!selectedImage && hasHdOption && selectedImageCount === 0)
                  }
                  isHdOptionEnabled={
                    selectedImage ? selectedImage.isHd : hasHdOption
                  }
                  hdOptionSelected={hdOptionSelected}
                  onHdOptionChange={(checked) => {
                    setHdOptionSelected(checked as boolean);
                  }}
                  setShowDialog={setShowDialog}
                />
              )}
            {!simplified &&
              genericImageConfigured &&
              !selectedImage &&
              selectedImageCount === 0 && (
              <div className="flex items-center gap-2 mt-2">
                <Checkbox
                  id="insert-generic-image"
                  checked={isGenericImage}
                  disabled={batchUploadActive}
                  onCheckedChange={(checked) => {
                    const nextValue = checked === true;
                    form.setValue("isGenericImage", nextValue, {
                      shouldValidate: true,
                    });
                    if (nextValue) {
                      form.setValue("images", []);
                    }
                  }}
                />
                <Label htmlFor="insert-generic-image">
                  {t("attachment.genericImage.insert")}
                </Label>
              </div>
            )}
          </div>
          {inputName && (
            <FormField
              control={control}
              name={"name"}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      {...field}
                      label={fieldLabel("name")}
                      id={field.name}
                      type="text"
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          )}
          {/*<HdOption*/}
          {/*  hdOptionEnabled={forfait?.hdOption ?? false}*/}
          {/*  hdOptionSelected={hdSelected}*/}
          {/*  onHdOptionChange={setHdSelected}*/}
          {/*  className="z-20"*/}
          {/*/>*/}
          {!selectedImage && selectedImageCount > 1 ? null : (
            <FormField
              control={control}
              name={"name"}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      {...field}
                      label={fieldLabel("name")}
                      id={field.name}
                      type="text"
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          )}
          {mainPositionMultiBlocked && (
            <p className="text-sm text-red-600">
              {t("mediaLibrary.batch.mainPositionSingleOnly")}
            </p>
          )}
          <div
            className={cn(
              "flex flex-col gap-4",
              batchUploadActive && "opacity-60 pointer-events-none",
            )}
          >
          {!simplified && (
          <MultilingualField
            defaultLang={getMainLanguage()}
            render={(lang) => (
              <Col>
                <FormField
                  control={control}
                  name={`caption.${lang}`}
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          {...field}
                          label={fieldLabel("caption")}
                          id={field.name}
                          type="text"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                {drawerMediaKind === "image" && (
                  <FormField
                    control={control}
                    name={`textAlt.${lang}`}
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input
                            {...field}
                            label={t("attachment.form.textAlt")}
                            id={field.name}
                            type="text"
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                )}
              </Col>
            )}
          />
          )}
          <FormField
            control={control}
            name={"position"}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Select
                    onValueChange={(value) => {
                      if (isPositionAlreadyAssignedToOther(value)) {
                        setOpenFacePositionWarning(true);
                        return;
                      }
                      field.onChange(value);
                      if (isMainPosition(value)) {
                        onFacePositionTriggered();
                      }
                    }}
                    value={field.value}
                  >
                    <Label
                      htmlFor={field.name}
                      className="flex text-start mb-1.5 text-slate-700 text-sm font-medium font-['Raleway'] leading-normal"
                    >
                      {t("attachment.form.position")}
                      <div className="text-red-500">*</div>
                    </Label>
                    <p className="text-xs text-red-500 mb-1.5 text-start font-['Raleway'] leading-normal">
                      {vt(
                        "attachment.form.position.hint",
                        'La position "Face" détermine l\'image principale.',
                      )}
                    </p>
                    <SelectTrigger className="border-red-500">
                      <SelectValue
                        placeholder={t("attachment.form.position.placeholder")}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {optionsPosition[type].map((position: string) => (
                        <SelectItem key={position} value={position}>
                          {t(position)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name={"published"}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Select
                    onValueChange={(v) => field.onChange(v === "true")}
                    value={field.value === true ? "true" : "false"}
                  >
                    <Label
                      htmlFor={field.name}
                      className="flex text-start mb-1.5 text-slate-700 text-sm font-medium font-['Raleway'] leading-normal"
                    >
                      {t(
                        usePluralPublicationLabels
                          ? fieldLabel("publishedLabelPlural")
                          : fieldLabel("publishedLabel"),
                      )}
                    </Label>
                    <SelectTrigger>
                      <SelectValue
                        placeholder={fieldLabel("publishedPlaceholder")}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="true">
                        {t(
                          usePluralPublicationLabels
                            ? fieldLabel("publishedYesPlural")
                            : fieldLabel("publishedYes"),
                        )}
                      </SelectItem>
                      <SelectItem value="false">
                        {t(
                          usePluralPublicationLabels
                            ? fieldLabel("publishedNoPlural")
                            : fieldLabel("publishedNo"),
                        )}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </FormControl>
              </FormItem>
            )}
          />
          {!simplified &&
            watermarkModuleEnabled &&
            !isGenericImage &&
            !isVideoMediaContext && (
            <FormField
              control={control}
              name="applyWatermark"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Select
                      onValueChange={(v) => field.onChange(v === "true")}
                      value={field.value !== false ? "true" : "false"}
                    >
                      <Label
                        htmlFor={field.name}
                        className="flex text-start mb-1.5 text-slate-700 text-sm font-medium font-['Raleway'] leading-normal"
                      >
                        {t("attachment.applyWatermark")}
                      </Label>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="true">
                          {t("attachment.applyWatermarkYes")}
                        </SelectItem>
                        <SelectItem value="false">
                          {t("attachment.applyWatermarkNo")}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                </FormItem>
              )}
            />
          )}
          {!simplified && showVideoLoopOption && (
            <FormField
              control={control}
              name="videoLoop"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between gap-3 rounded-md border p-3">
                  <Label htmlFor="media-library-video-loop" className="text-sm font-medium">
                    {t("cms.generalSettings.player.loop")}
                  </Label>
                  <FormControl>
                    <Switch
                      id="media-library-video-loop"
                      checked={field.value ?? false}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          )}
          {!simplified && (
          <ModifiableList
            name="photoCredits"
            listKey="liste-credits"
            label={fieldLabel("photoCredits")}
          />
          )}
          {!simplified && (
          <MultilingualField
            defaultLang={getMainLanguage()}
            render={(lang) => (
              <FormField
                control={control}
                name={`notes.${lang}`}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Tiptap
                        label={t("attachment.form.notes")}
                        description={field.value ?? ""}
                        onChange={(e: unknown) => {
                          field.onChange(typeof e === "string" ? e : "");
                        }}
                        toolbarPreset="complete"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            )}
          />
          )}
          </div>
          <Button
            type="submit"
            loading={loading}
            disabled={
              disabled ||
              batchUploadActive ||
              loading ||
              (!selectedImage && !form.formState.isValid) ||
              mainPositionMultiBlocked ||
              (selectedImageCount === 0 &&
                !selectedImage &&
                !isGenericImage)
            }
            className={
              selectedImageCount === 0 && !selectedImage && !isGenericImage
                ? "bg-gray-300 text-gray-700"
                : ""
            }
          >
            {loading && uploadProgress
              ? `${getBatchUploadDisplayCurrent(uploadProgress)} / ${uploadProgress.total} ${t("mediaLibrary.batch.uploadProgress")}`
              : selectedImage
                ? t(mediaLibraryTranslationKey("save", drawerMediaKind))
                : selectedImageCount > 1
                  ? t(
                      mediaLibraryTranslationKey("submitBatch", drawerMediaKind),
                      { count: selectedImageCount },
                    )
                  : t(mediaLibraryTranslationKey("submit", drawerMediaKind))}
          </Button>
        </form>
      </FormProvider>
      <ShowOptionsDialog
        open={showDialog}
        onOpenChange={(open) => setShowDialog(open)}
        forfait={forfait ?? ({} as Forfait)}
        setSelectedOptionName={subscribeToOptionSuccess}
      />
      <Dialog open={openConfirmation} onOpenChange={setOpenConfirmation}>
        <DialogContent className="p-6">
          <div className="flex items-center justify-center my-4">
            <CheckCircleIcon className="w-8 h-8 text-primary" />
          </div>
          <div className="text-center text-2xl mx-4">
            {t("optionAjoutSuccesTitre1")}{" "}
            <strong>"{selectedOptionName}"</strong>{" "}
            {t("optionAjoutSuccesTitre2")}
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={showWarningDialog} onOpenChange={setShowWarningDialog}>
        <DialogContent className="max-w-md">
          <div className="text-center text-lg font-bold">
            {t("warningHdDialogDescription")}
          </div>
          <DialogClose>
            <div className="flex justify-between gap-2">
              <Button
                variant="outline"
                className="w-full"
                onClick={() => setShowWarningDialog(false)}
              >
                {t("formCancel")}
              </Button>
              <Button
                className="w-full"
                onClick={() => {
                  setShowWarningDialog(false);
                  form.handleSubmit(
                    (data) => {
                      data.isHd = hdOptionSelected && hasHdOption;
                      setHdOptionSelected(true);
                      onSubmit(data);
                    },
                    (errors) => {
                      console.log(errors);
                    },
                  )();
                }}
              >
                {t("confirmAddAttachmentDialog")}
              </Button>
            </div>
          </DialogClose>
        </DialogContent>
      </Dialog>
      <ConfirmActionDialog
        open={openConfirmationDelete}
        onOpenChange={setOpenConfirmationDelete}
        message={t("imageSuppressionQuestion")}
        cancelLabel={t("formCancel")}
        confirmLabel={t("confirmAddAttachmentDialog")}
        onConfirm={() => {
          onDelete?.(selectedImage?.id ?? "");
          const deleteKind = resolveMediaLibraryKind({
            attachment: selectedImage,
          });
          toast({
            title: t(
              mediaLibraryTranslationKey("deleteSuccessTitle", deleteKind),
            ),
            variant: "success",
            description: t(
              mediaLibraryTranslationKey("deleteSuccessDescription", deleteKind),
            ),
          });
          setOpen(false);
        }}
      />
      <ConfirmActionDialog
        open={openFacePositionWarning}
        onOpenChange={setOpenFacePositionWarning}
        message={t("positionFaceUniqueWarning")}
        confirmLabel={t("formOk")}
        onConfirm={() => {}}
        contentClassName="p-6 max-w-md"
      />
    </StyledDrawer>
  );
};

interface MediaLibraryProps {
  id: string;
  type: TypeOptionPosition;
  invalidateOnChange?: () => void;
  computeImageName?: (data?: ImageFormData) => string;
  embedAttachmentOnSubmit?: boolean;
  setImageMode: (imageMode: boolean) => void;
  copyFromMaster?: CopyFromMasterConfig;
  /** When true, adapts layout for use inside a StyledDrawer (simplified header, responsive grid). */
  embeddedInDrawer?: boolean;
  /**
   * Compact inline media manager for the simplified sheet view: renders a
   * thumbnail strip + an "Ajouter" tile that opens the simplified add-image
   * drawer directly (no full-page toolbar/grid). Reuses the same upload flow.
   * Implies {@link simplifiedDrawer}.
   */
  simplified?: boolean;
  /**
   * Keeps the full-page management layout (grid/list/toolbar) but makes the
   * add/edit drawer use the simplified field set. Used by the "Gérer les
   * photos" page when the sheet is in Essentiel mode.
   */
  simplifiedDrawer?: boolean;
}

const MediaLibrary = ({
  id,
  type,
  invalidateOnChange,
  computeImageName,
  embedAttachmentOnSubmit = false,
  setImageMode,
  copyFromMaster,
  embeddedInDrawer = false,
  simplified = false,
  simplifiedDrawer = false,
}: MediaLibraryProps) => {
  // The compact inline layout also uses the simplified drawer.
  const useSimplifiedDrawer = simplified || simplifiedDrawer;
  const { instanceData } = useAuth();
  const [open, setOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<Attachment | null>(null);

  // HD logic
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [imageToDelete, setImageToDelete] = useState<string | null>(null);
  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  const [errorContent, setErrorContent] = useState<string | null>(null);

  // Copyright preview modal: which attachment's copyright version is shown
  const [copyrightPreviewAttachment, setCopyrightPreviewAttachment] =
    useState<Attachment | null>(null);
  // The watermarked object can be missing in S3 even when the API advertised a
  // URL; without this the modal would just show an empty frame.
  const [copyrightPreviewFailed, setCopyrightPreviewFailed] = useState(false);
  const [retouchAttachment, setRetouchAttachment] =
    useState<Attachment | null>(null);

  const { t } = useTranslation();
  const vt = useVisibleT();
  const { updateProgressToast, dismissProgressToast } =
    useBatchUploadProgressToast();
  const [uploadProgress, setUploadProgress] =
    useState<BatchUploadProgress | null>(null);
  const [batchUploadActive, setBatchUploadActive] = useState(false);
  const drawerOpenRef = useRef(false);
  const uploadProgressRef = useRef<BatchUploadProgress | null>(null);
  drawerOpenRef.current = open;

  const hasCopyrightVersion = (a: Attachment) =>
    !!(
      a.copyrightMediumPublicUrl ??
      a.copyrightLargePublicUrl ??
      a.copyrightSmallPublicUrl
    );

  const copyrightPreviewUrl = copyrightPreviewAttachment
    ? (copyrightPreviewAttachment.copyrightLargePublicUrl ??
      copyrightPreviewAttachment.copyrightMediumPublicUrl ??
      copyrightPreviewAttachment.copyrightSmallPublicUrl)
    : null;

  const columns: ColumnDef<Attachment>[] = [
    {
      accessorKey: "publicUrl",
      header: t("gestionImagesTableImage"),
      cell: ({ row }) =>
        row.original.isGenericImage ? (
          <div className="w-[50px] h-[50px]">
            <GenericImagePreview
              settings={genericImageSettings}
              className="w-full h-full"
            />
          </div>
        ) : (
          <div className="flex h-[50px] w-[50px] items-center justify-center">
            <img
              src={
                getAttachmentPreviewUrl(row.original) ??
                ""
              }
              alt=""
              className="max-h-full max-w-full object-contain"
            />
          </div>
        ),
    },
    // {
    //   accessorKey: "name",
    //   header: t("gestionImagesTableNom"),
    // },
    {
      cell: ({ row }) => t(row.original.position),
      header: t("gestionImagesTablePosition"),
    },
    {
      cell: ({ row }) =>
        row.original.published
          ? t("publishedFrontPublic")
          : t("imageFieldNotPublished"),
      header: t("gestionImagesTablePublished"),
    },
  ];

  const {
    data: attachments,
    isLoading,
    refetch,
  } = useGetAttachmentsByDocId(id, { pollWhileAnyVideoProcessing: true });

  const { mutateAsync: updateAttachment } = useUpdateAttachment();
  const { mutate: deleteAttachment } = useDeleteAttachment();
  const { mutateAsync: embedAttachment } = useEmbedAttachment();
  const { mutateAsync: deleteHdAttachment } = useDeleteHdAttachment();
  const { mutateAsync: reorderAttachments } = useReorderAttachments();
  const { mutateAsync: createGenericAttachment } =
    useCreateGenericImageAttachment();
  const { data: genericImageSettings } = useGetGenericImageSettings();
  const { user } = useAuth();
  const isReader = user?.role === "reader" ? true : false;

  const [masterPickerOpen, setMasterPickerOpen] = useState(false);
  const [embedDrawerOpen, setEmbedDrawerOpen] = useState(false);
  const hasMasterImport = Boolean(copyFromMaster?.masterDocId);
  const { data: masterAttachments, isLoading: isLoadingMaster } =
    useGetAttachmentsByDocId(
      hasMasterImport ? copyFromMaster!.masterDocId : undefined,
    );
  const masterImageCount = useMemo(
    () => (masterAttachments ?? []).filter((a) => !a.isGenericImage).length,
    [masterAttachments],
  );

  const openNewImageDrawer = () => {
    form.reset({
      name: "",
      textAlt: {},
      caption: {},
      notes: {},
      photoCredits: "",
      position: "",
      published: false,
      applyWatermark: true,
      isGenericImage: false,
      videoLoop: false,
      images: [],
    });
    setSelectedImage(null);
    setOpen(true);
  };

  const serverSorted = useMemo(
    () =>
      [...(attachments ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [attachments],
  );

  const [optimisticOrder, setOptimisticOrder] = useState<Attachment[] | null>(
    null,
  );

  useEffect(() => {
    setOptimisticOrder(null);
  }, [attachments]);

  useEffect(() => {
    if (!selectedImage?.id || !attachments) return;
    const fresh = attachments.find((attachment) => attachment.id === selectedImage.id);
    if (!fresh) return;

    const hasNewerVideoData =
      fresh.videoStatus !== selectedImage.videoStatus ||
      fresh.videoPublicUrl !== selectedImage.videoPublicUrl ||
      fresh.posterPublicUrl !== selectedImage.posterPublicUrl ||
      fresh.mediumPublicUrl !== selectedImage.mediumPublicUrl ||
      fresh.largePublicUrl !== selectedImage.largePublicUrl;

    if (hasNewerVideoData) {
      setSelectedImage(fresh);
    }
  }, [attachments, selectedImage]);

  const sortedAttachments = optimisticOrder ?? serverSorted;

  const galleryImages = useMemo(
    () => attachmentsToGalleryImages(sortedAttachments),
    [sortedAttachments],
  );

  const handleReorder = useCallback(
    (event: { activeIndex: number; overIndex: number }) => {
      const reordered = arrayMove(
        sortedAttachments,
        event.activeIndex,
        event.overIndex,
      );
      setOptimisticOrder(reordered);

      const items = reordered.map((a, i) => ({ id: a.id, order: i + 1 }));
      reorderAttachments(items).then(() => {
        refetch();
        invalidateOnChange?.();
      });
    },
    [sortedAttachments, reorderAttachments, refetch, invalidateOnChange],
  );

  const form = useForm<ImageFormData>({
    resolver: zodResolver(imageSchema),
    reValidateMode: "onChange",
    defaultValues: {
      name: "",
      textAlt: {},
      caption: {},
      notes: {},
      photoCredits: "",
      position: "",
      published: false,
      applyWatermark: true,
      isGenericImage: false,
      videoLoop: false,
      images: [],
    },
  });

  const handleDrawerOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen && batchUploadActive) {
        setOpen(false);
        if (uploadProgressRef.current) {
          updateProgressToast(uploadProgressRef.current);
        }
        return;
      }
      if (!nextOpen) {
        form.reset({
          name: "",
          textAlt: {},
          caption: {},
          notes: {},
          photoCredits: "",
          position: "",
          published: false,
          applyWatermark: true,
          isGenericImage: false,
          videoLoop: false,
          images: [],
        });
        setSelectedImage(null);
      }
      setOpen(nextOpen);
    },
    [batchUploadActive, uploadProgress, updateProgressToast, form],
  );

  const publishUploadProgress = useCallback(
    (progress: BatchUploadProgress) => {
      uploadProgressRef.current = progress;
      setUploadProgress(progress);
      if (!drawerOpenRef.current) {
        updateProgressToast(progress);
      }
    },
    [updateProgressToast],
  );

  const UploadImage = async (data: ImageFormData) => {
    if (data.isGenericImage) {
      try {
        const maxOrder = Math.max(
          0,
          ...(attachments ?? []).map((a) => a.order ?? 0),
        );
        const attachment = await createGenericAttachment({
          docId: id,
          position: data.position,
          name: computeImageName?.(data) ?? "",
          displayName: data.name,
          caption: data.caption,
          textAlt: data.textAlt,
          photoCredits: data.photoCredits,
          notes: normalizeAttachmentNotes(data.notes),
          published: data.published,
          order: maxOrder + 1,
        });
        toast({
          variant: "success",
          description: t(mediaLibraryTranslationKey("uploadSuccess", "image")),
        });
        if (
          embedAttachmentOnSubmit &&
          attachment.id &&
          shouldTriggerArtmatchEmbed(
            { position: "", published: false },
            {
              position: data.position,
              published: data.published,
              images: data.images,
            },
          )
        ) {
          runArtmatchEmbedInBackground(embedAttachment, attachment.id);
        }
        refetch();
        invalidateOnChange?.();
        setOpen(false);
      } catch (e) {
        if (isAxiosError(e)) {
          setOpenErrorDialog(true);
          setErrorContent(extractUploadErrorMessage(e));
          return;
        }
        form.setError("root", { message: t("unknownError") });
        throw e;
      }
      return;
    }

    const files = sortFilesByNameNatural(data.images ?? []);
    if (files.length === 0) return;

    if (instanceData == null) {
      console.error("Tried to upload an image, but instanceData is null");
      return;
    }

    if (selectedImage && !data.isHd && selectedImage.isHd) {
      deleteHdAttachment(selectedImage?.id ?? "", {
        onSuccess: () => {
          invalidateOnChange?.();
        },
      });
    }

    const baseOrder = Math.max(
      0,
      ...(attachments ?? []).map((a) => a.order ?? 0),
    );

    let progressItems = files.map((file) => ({
      fileName: file.name,
      status: "pending" as BatchFileUploadStatus,
    }));

    let successCount = 0;
    setBatchUploadActive(true);
    publishUploadProgress({
      current: 0,
      total: files.length,
      items: progressItems,
    });

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const displayName =
          files.length === 1 && data.name?.trim()
            ? data.name.trim()
            : stripExtension(file.name);
        const perFileData = { ...data, name: displayName };

        progressItems = progressItems.map((item, idx) =>
          idx === i ? { ...item, status: "uploading" as const } : item,
        );
        publishUploadProgress({
          current: successCount,
          total: files.length,
          items: progressItems,
        });

        const mediaName = computeImageName?.(perFileData) ?? "";

        if (isVideoFile(file)) {
          // Video: async transcode via the converter (no artmatch embed).
          await uploadVideo(
            file,
            {
              name: mediaName,
              displayName,
              docId: id,
              position: data.position,
              instanceId: instanceData.id,
              order: baseOrder + i + 1,
              published: data.published,
              videoLoop: data.videoLoop,
              caption: perFileData.caption,
              photoCredits: perFileData.photoCredits,
              notes: normalizeAttachmentNotes(perFileData.notes),
            },
            instanceData.s3Bucket,
          );
        } else {
          const uploadEndpoint = isMainPosition(data.position)
            ? "back/api/attachment/create/unique"
            : "back/api/attachment/create";
          const attachment = await uploadImage(
            file,
            {
              ...data,
              published: data.published,
              position: data.position,
              docId: id,
              details: "",
              name: mediaName,
              isHd: data.isHd,
              applyWatermark: data.applyWatermark ?? true,
              order: baseOrder + i + 1,
            } as UploadAttachmentPayload,
            instanceData.s3Bucket,
            instanceData.id,
            uploadEndpoint,
            displayName,
          );

          if (
            embedAttachmentOnSubmit &&
            attachment.id &&
            shouldTriggerArtmatchEmbed(
              { position: "", published: false },
              {
                position: data.position,
                published: data.published,
                images: data.images,
              },
            )
          ) {
            runArtmatchEmbedInBackground(embedAttachment, attachment.id);
          }
        }
        successCount++;
        progressItems = progressItems.map((item, idx) =>
          idx === i ? { ...item, status: "done" as const } : item,
        );
        publishUploadProgress({
          current: successCount,
          total: files.length,
          items: progressItems,
        });
      }

      dismissProgressToast();
      const uploadKind = resolveMediaLibraryKind({ files });
      toast({
        variant: "success",
        description:
          files.length > 1
            ? t(
                mediaLibraryTranslationKey("uploadSuccessBatch", uploadKind),
                { count: files.length },
              )
            : t(mediaLibraryTranslationKey("uploadSuccess", uploadKind)),
      });
      refetch();
      invalidateOnChange?.();
      form.reset({
        name: "",
        textAlt: {},
        caption: {},
        notes: {},
        photoCredits: "",
        position: "",
        published: false,
        applyWatermark: true,
        isGenericImage: false,
        videoLoop: false,
        images: [],
      });
      setOpen(false);
    } catch (e) {
      setUploadProgress((prev) => {
        if (!prev) return prev;
        const items = prev.items.map((item, idx) =>
          idx === successCount && item.status === "uploading"
            ? { ...item, status: "error" as BatchFileUploadStatus }
            : item,
        );
        return { ...prev, items };
      });
      dismissProgressToast();
      if (successCount > 0) {
        toast({
          description: t("mediaLibrary.batch.uploadPartial", {
            success: successCount,
            total: files.length,
          }),
        });
        refetch();
        invalidateOnChange?.();
      }
      if (isAxiosError(e)) {
        setOpenErrorDialog(true);
        setErrorContent(extractUploadErrorMessage(e));
        return;
      }
      form.setError("root", { message: t("unknownError") });
      throw e;
    } finally {
      setBatchUploadActive(false);
      setUploadProgress(null);
      uploadProgressRef.current = null;
    }
  };

  const updateImage = async (attachment: Attachment) => {
    setSelectedImage(attachment);
    const multilingual = hydrateAttachmentMultilingualFields(attachment);
    form.reset({
      ...attachment,
      notes: attachment.notes ?? {},
      caption: multilingual.caption ?? {},
      textAlt: multilingual.textAlt ?? {},
      videoLoop: attachment.videoLoop ?? false,
      images: [],
    });
    void form.trigger();
    setOpen(true);
  };

  const handleAttachmentDeleted = () => {
    setOptimisticOrder(null);
    refetch();
    invalidateOnChange?.();
  };

  const deleteImage = async () => {
    if (imageToDelete) {
      const deleteKind = resolveMediaLibraryKind({
        attachment: attachments?.find((a) => a.id === imageToDelete),
      });
      deleteAttachment(imageToDelete, {
        onSuccess: () => {
          toast({
            title: t(
              mediaLibraryTranslationKey("deleteSuccessTitle", deleteKind),
            ),
            variant: "success",
            description: t(
              mediaLibraryTranslationKey("deleteSuccessDescription", deleteKind),
            ),
          });
          handleAttachmentDeleted();
        },
      });
    }
  };

  const handleImageEdit = async (data: ImageFormData) => {
    if (selectedImage) {
      try {
        const hydrated = hydrateAttachmentMultilingualFields(selectedImage);
        const metadataOverrides = buildAttachmentMetadataOverrides(
          selectedImage,
          { caption: data.caption, textAlt: data.textAlt },
          form.formState.dirtyFields,
        );
        await updateAttachment({
          id: selectedImage.id ?? "",
          attachment: buildAttachmentUpdatePayload(
            { ...selectedImage, ...hydrated },
            {
              name: data.name,
              position: data.position,
              docId: id,
              photoCredits: data.photoCredits,
              notes: normalizeAttachmentNotes(data.notes),
              published: data.published,
              isHd: data.isHd,
              applyWatermark: data.applyWatermark,
              isGenericImage: data.isGenericImage,
              ...metadataOverrides,
              ...(selectedImage.kind === "video" ||
              selectedImage.kind === "embed"
                ? { videoLoop: data.videoLoop ?? false }
                : {}),
            },
          ),
        });
        if (
          embedAttachmentOnSubmit &&
          selectedImage.id &&
          shouldTriggerArtmatchEmbed(selectedImage, {
            position: data.position,
            published: data.published,
            images: data.images,
          })
        ) {
          runArtmatchEmbedInBackground(embedAttachment, selectedImage.id);
        }
        const editKind = resolveMediaLibraryKind({ attachment: selectedImage });
        toast({
          title: t(mediaLibraryTranslationKey("updateSuccessTitle", editKind)),
          variant: "success",
          description: t(
            mediaLibraryTranslationKey("updateSuccessDescription", editKind),
          ),
        });
        refetch();
        invalidateOnChange?.();
        setOpen(false);
      } catch (e) {
        console.error(e);
        if (isAxiosError(e)) {
          setOpenErrorDialog(true);
          setErrorContent(extractUploadErrorMessage(e));
          return;
        }
        const editKind = resolveMediaLibraryKind({ attachment: selectedImage });
        toast({
          title: t(mediaLibraryTranslationKey("updateErrorTitle", editKind)),
          variant: "error",
          description: t(
            mediaLibraryTranslationKey("updateErrorDescription", editKind),
          ),
        });
      }
    }
  };

  const showAddDropdown = hasMasterImport || Boolean(instanceData);

  const uploadOrEditPicture = (
    <UploadOrEditPicture
      form={form}
      open={open}
      setOpen={setOpen}
      selectedImage={selectedImage}
      setSelectedImage={setSelectedImage}
      onSubmit={selectedImage ? handleImageEdit : UploadImage}
      onDelete={
        selectedImage
          ? (attachmentId) =>
              deleteAttachment(attachmentId, {
                onSuccess: () => {
                  setOpen(false);
                  setSelectedImage(null);
                  handleAttachmentDeleted();
                },
              })
          : undefined
      }
      type={type}
      disabled={isReader}
      watermarkModuleEnabled={instanceData?.watermarkEnabled === true}
      attachments={attachments ?? undefined}
      hideTrigger={simplified || showAddDropdown}
      uploadProgress={uploadProgress}
      batchUploadActive={batchUploadActive}
      onDrawerOpenChange={handleDrawerOpenChange}
      embeddedInDrawer={embeddedInDrawer}
      simplified={useSimplifiedDrawer}
      onRetouch={
        isReader
          ? undefined
          : (attachment) => setRetouchAttachment(attachment)
      }
    />
  );

  const imageStudioHost = (
    <ImageStudioHost
      attachment={retouchAttachment}
      open={!!retouchAttachment}
      onOpenChange={(next) => {
        if (!next) setRetouchAttachment(null);
      }}
      onSaved={() => {
        refetch();
        invalidateOnChange?.();
      }}
    />
  );

  const toolbarActions = (
    <>
      {showAddDropdown ? (
        <Menubar className="border-none p-0 h-auto">
          <MenubarMenu>
            <MenubarTrigger
              className={cn(
                "font-semibold flex gap-2 px-3 py-2 h-9 rounded-md border-none bg-primary text-white shadow hover:bg-primary/90 hover:text-white data-[state=open]:bg-primary/90 data-[state=open]:text-white",
                isReader && "opacity-50 cursor-default",
              )}
              disabled={isReader}
            >
              <PlusIcon className="h-4 w-4" strokeWidth={2.5} />
              {t(mediaLibraryTranslationKey("button", "image"))}
              <ChevronDownIcon className="w-4 h-4" />
            </MenubarTrigger>
            <MenubarContent>
              <MenubarItem onClick={openNewImageDrawer}>
                {t("mediaLibrary.addImage.new")}
              </MenubarItem>
              {hasMasterImport && (
                <MenubarItem
                  disabled={
                    isReader || isLoadingMaster || masterImageCount === 0
                  }
                  onClick={() => setMasterPickerOpen(true)}
                >
                  {t("mediaLibrary.addImage.fromMaster")}
                </MenubarItem>
              )}
              {instanceData && (
                <MenubarItem
                  disabled={isReader}
                  onClick={() => setEmbedDrawerOpen(true)}
                >
                  {t("mediaLibrary.embed.add")}
                </MenubarItem>
              )}
            </MenubarContent>
          </MenubarMenu>
        </Menubar>
      ) : (
        uploadOrEditPicture
      )}
      {showAddDropdown && uploadOrEditPicture}
      {hasMasterImport && copyFromMaster && (
        <ImportFromMasterDrawers
          pickerOpen={masterPickerOpen}
          onPickerOpenChange={setMasterPickerOpen}
          copyFromMaster={copyFromMaster}
          targetDocId={id}
          childType={type}
          childAttachments={attachments ?? []}
          computeImageName={computeImageName}
          onDuplicated={() => {
            refetch();
            invalidateOnChange?.();
          }}
          embedAttachmentOnSubmit={embedAttachmentOnSubmit}
          disabled={isReader}
        />
      )}
      {instanceData && (
        <AddEmbedForm
          docId={id}
          type={type}
          instanceId={instanceData.id}
          onSuccess={() => {
            refetch();
            invalidateOnChange?.();
          }}
          disabled={isReader}
          hideTrigger={showAddDropdown}
          open={showAddDropdown ? embedDrawerOpen : undefined}
          onOpenChange={showAddDropdown ? setEmbedDrawerOpen : undefined}
          embeddedInDrawer={embeddedInDrawer}
        />
      )}
    </>
  );

  const viewToggle = (
    <TabsList className="grid grid-cols-2">
      <TabsTrigger value="grid">
        <LayoutGrid className="h-4 w-4" />
      </TabsTrigger>
      <TabsTrigger value="list">
        <Table2 className="h-4 w-4" />
      </TabsTrigger>
    </TabsList>
  );

  const tileClassName = embeddedInDrawer
    ? "relative flex aspect-square w-full cursor-pointer items-center justify-center overflow-hidden rounded-lg bg-gray-100"
    : "relative flex h-[200px] w-[200px] cursor-pointer items-center justify-center overflow-hidden rounded-lg bg-gray-100";

  const gridContainerClassName = embeddedInDrawer
    ? "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3"
    : "flex gap-2 flex-wrap";

  const metadataClassName = embeddedInDrawer
    ? "flex items-start gap-1 w-full mt-1"
    : "flex items-start gap-1 w-[200px] mt-1";

  if (simplified) {
    return (
      <>
        <div className="flex flex-wrap gap-3">
          {sortedAttachments.map((attachment) => {
            const isMotion =
              attachment.kind === "video" || attachment.kind === "embed";
            // Embeds (YouTube/Vimeo) have no uploaded image URL; their thumbnail
            // is resolved from the provider/video id, like the full-page grid.
            const previewUrl =
              attachment.kind === "embed"
                ? (resolveEmbedThumbnailUrl(
                    attachment.embedProvider,
                    attachment.embedVideoId,
                    attachment.embedUrl,
                  ) ?? undefined)
                : getAttachmentPreviewUrl(attachment);
            return (
              <button
                type="button"
                key={attachment.id}
                onClick={() => updateImage(attachment)}
                disabled={isReader}
                className="relative flex h-[120px] w-[120px] items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-slate-50 disabled:cursor-default"
              >
                {attachment.isGenericImage ? (
                  <GenericImagePreview
                    settings={genericImageSettings}
                    className="h-full w-full"
                  />
                ) : previewUrl ? (
                  <img
                    src={previewUrl}
                    alt=""
                    className="max-h-full max-w-full object-contain"
                  />
                ) : (
                  <ImageIcon className="h-6 w-6 text-slate-300" />
                )}
                {isMotion && (
                  <span className="absolute inset-0 flex items-center justify-center">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-black/50">
                      <Play className="h-4 w-4 fill-white text-white" />
                    </span>
                  </span>
                )}
                {attachment.published && (
                  <span className="absolute bottom-1 left-1 rounded bg-green-500/90 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                    {t("publishedFrontPublic")}
                  </span>
                )}
              </button>
            );
          })}
          {!isReader && (
            <button
              type="button"
              onClick={openNewImageDrawer}
              className="flex h-[120px] w-[120px] flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 hover:bg-slate-100"
            >
              <Plus className="h-20 w-20 text-primary" strokeWidth={3} />
              <span className="px-2 text-center text-sm font-semibold text-slate-500">
                {t(mediaLibraryTranslationKey("button", "image"))}
              </span>
            </button>
          )}
        </div>
        {uploadOrEditPicture}
        {imageStudioHost}
        <Dialog open={openErrorDialog} onOpenChange={setOpenErrorDialog}>
          <DialogContent className="p-6">
            <VisuallyHidden>
              <DialogTitle className="sr-only">Error</DialogTitle>
            </VisuallyHidden>
            <div className="flex items-center justify-center my-4">
              <XCircleIcon className="w-8 h-8 text-red-500" />
            </div>
            <div className="text-center text-2xl mx-4">
              {errorContent
                ? formatUploadErrorForDisplay(errorContent, t)
                : t("unknownError")}
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <Tabs defaultValue={"grid"}>
      <div className="flex items-center justify-between gap-2 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <Button
            type="button"
            variant="white"
            className="text-white text-sm flex gap-2 bg-primary hover:bg-primary/90 border-none shrink-0"
            onClick={() => {
              setImageMode(false);
            }}
          >
            <ChevronLeft className="h-4 w-4" />
            {t("mediaLibrary.backToSheet")}
          </Button>
          <h1
            className={cn(
              "font-semibold truncate",
              embeddedInDrawer ? "text-xl" : "text-[32px]",
            )}
          >
            {t("titreGestionImages")}
          </h1>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {viewToggle}
          {toolbarActions}
        </div>
      </div>
      <Separator className="my-4" />
      <TabsContent value="grid">
        {isLoading && <p>{t("chargement")}</p>}
        {!isLoading && (
          <Sortable
            value={sortedAttachments}
            onMove={handleReorder}
            orientation="mixed"
          >
            <div className={gridContainerClassName}>
              {sortedAttachments.map((attachment: Attachment) => {
                const previewUrl = getAttachmentPreviewUrl(attachment);
                return (
                <SortableItem key={attachment.id} value={attachment.id} className={embeddedInDrawer ? "min-w-0" : undefined}>
                  <div className={tileClassName}>
                    {attachment.isGenericImage && (
                      <GenericImagePreview
                        settings={genericImageSettings}
                        className="h-full w-full"
                      />
                    )}
                    {!attachment.isGenericImage &&
                      attachment.kind !== "embed" &&
                      previewUrl && (
                        <div className="flex h-full w-full items-center justify-center">
                          <UniqueImageFullScreen
                            media={{
                              url: previewUrl,
                              largeUrl:
                                attachment.kind === "video"
                                  ? (attachment.videoPublicUrl ??
                                    attachment.largePublicUrl ??
                                    previewUrl)
                                  : (attachment.largePublicUrl ??
                                    previewUrl),
                              kind: attachment.kind,
                              videoUrl: attachment.videoPublicUrl,
                              alt: "",
                              className: "max-h-full max-w-full object-contain",
                            }}
                            gallery={
                              galleryImages.length > 0
                                ? galleryImages
                                : undefined
                            }
                            initialIndex={galleryIndexForAttachment(
                              galleryImages,
                              attachment.id,
                            )}
                          />
                        </div>
                      )}
                    {/* Video being converted (no poster yet) */}
                    {!attachment.isGenericImage &&
                      attachment.kind === "video" &&
                      !previewUrl && (
                        <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-gray-500">
                          <Film className="w-8 h-8" />
                          <span className="text-xs text-center px-2">
                            {attachment.videoStatus === "error"
                              ? t("mediaLibrary.video.conversionError")
                              : t("mediaLibrary.video.processing")}
                          </span>
                        </div>
                      )}
                    {/* Embed (YouTube/Vimeo): clickable static thumbnail (no autoplay) */}
                    {!attachment.isGenericImage &&
                      attachment.kind === "embed" && (
                        <div className="flex h-full w-full items-center justify-center">
                          <UniqueImageFullScreen
                            media={{
                              url:
                                resolveEmbedThumbnailUrl(
                                  attachment.embedProvider,
                                  attachment.embedVideoId,
                                  attachment.embedUrl,
                                ) ?? "",
                              largeUrl: "",
                              kind: "embed",
                              embedProvider: attachment.embedProvider,
                              embedVideoId: attachment.embedVideoId,
                              embedUrl: attachment.embedUrl,
                              alt: attachment.name || "",
                              className: "max-h-full max-w-full object-contain",
                            }}
                            gallery={
                              galleryImages.length > 0
                                ? galleryImages
                                : undefined
                            }
                            initialIndex={galleryIndexForAttachment(
                              galleryImages,
                              attachment.id,
                            )}
                          />
                        </div>
                      )}
                    <div className="absolute bottom-1 right-1 flex gap-1">
                    {canRetouchAttachment(attachment) && !isReader && (
                      <Button
                        variant="white"
                        className="text-gray-950 text-sm flex gap-2"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setRetouchAttachment(attachment);
                        }}
                        title={vt("imageStudio.retouch", "Retoucher")}
                        aria-label={vt("imageStudio.retouch", "Retoucher")}
                      >
                        <Aperture className="w-4 h-4" />
                      </Button>
                    )}
                    <Button
                      variant="white"
                      className="text-gray-950 text-sm flex gap-2"
                      onClick={() => updateImage(attachment)}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    </div>
                    {attachment.isHd && (
                      <Crown className="w-5 h-5 text-[#E2CDA4] absolute top-3 right-3" />
                    )}
                    {hasCopyrightVersion(attachment) && (
                      <Button
                        variant="white"
                        className="absolute top-1 left-1 text-gray-950 text-sm flex gap-2"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setCopyrightPreviewFailed(false);
                          setCopyrightPreviewAttachment(attachment);
                        }}
                        title="Voir la version avec filigrane"
                        aria-label="Voir la version avec filigrane"
                      >
                        <Copyright className="w-4 h-4" />
                      </Button>
                    )}
                    <Button
                      className="absolute bottom-1 left-1 text-gray-950 text-sm flex gap-2"
                      variant="outline"
                      onClick={() => {
                        setImageToDelete(attachment.id);
                        setOpenConfirmation(true);
                      }}
                      disabled={isReader}
                    >
                      <Trash2 className="w-4 h-4 text-[#DC2626]" />
                    </Button>
                  </div>
                  <div className={metadataClassName}>
                    <SortableDragHandle
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 shrink-0 cursor-grab"
                    >
                      <GripVertical className="h-4 w-4 text-gray-400" />
                    </SortableDragHandle>
                    <div className="min-w-0">
                      <p className="text-sm text-gray-500 truncate">
                        {attachment.name}
                      </p>
                      <p className="text-sm text-gray-500">
                        {t(attachment.position)}
                      </p>
                      {attachment.published && (
                        <p className="text-sm text-green-500">
                          {t("publishedFrontPublic")}
                        </p>
                      )}
                      <p className="text-sm text-gray-500">
                        {t("attachment.info.orderPublication")}{" "}
                        {attachment.order}
                      </p>
                    </div>
                  </div>
                </SortableItem>
                );
              })}
              {!isReader && (
                <button
                  type="button"
                  onClick={openNewImageDrawer}
                  className={cn(
                    "flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 hover:bg-slate-100",
                    embeddedInDrawer
                      ? "aspect-square w-full min-w-0"
                      : "h-[200px] w-[200px]",
                  )}
                >
                  <Plus className="h-20 w-20 text-primary" strokeWidth={3} />
                  <span className="px-3 text-center text-sm font-semibold text-slate-500">
                    {t(mediaLibraryTranslationKey("button", "image"))}
                  </span>
                </button>
              )}
            </div>
          </Sortable>
        )}
      </TabsContent>
      <TabsContent value="list">
        <DataTable
          onRowClick={(row) => {
            updateImage(row.original);
          }}
          columns={columns}
          data={sortedAttachments}
          enableDragRows
          onRowDragged={(d) => {
            setOptimisticOrder(d.newData);
            const items = d.newData.map((a, i) => ({
              id: a.id,
              order: i + 1,
            }));
            reorderAttachments(items).then(() => {
              refetch();
              invalidateOnChange?.();
            });
          }}
          options={{
            enableColumnResizing: true,
          }}
        />
      </TabsContent>
      <ConfirmActionDialog
        open={openConfirmation}
        onOpenChange={setOpenConfirmation}
        message={t("imageSuppressionQuestion")}
        cancelLabel={t("formCancel")}
        confirmLabel={t("confirmAddAttachmentDialog")}
        onConfirm={() => {
          deleteImage();
          setImageToDelete(null);
        }}
      />
      <Dialog open={openErrorDialog} onOpenChange={setOpenErrorDialog}>
        <DialogContent className="p-6">
          <VisuallyHidden>
            <DialogTitle className="sr-only">Error</DialogTitle>
          </VisuallyHidden>
          <div className="flex items-center justify-center my-4">
            <XCircleIcon className="w-8 h-8 text-red-500" />
          </div>
          <div className="text-center text-2xl mx-4">
            {errorContent
              ? formatUploadErrorForDisplay(errorContent, t)
              : t("unknownError")}
          </div>
        </DialogContent>
      </Dialog>
      <Dialog
        open={!!copyrightPreviewAttachment}
        onOpenChange={(open) => !open && setCopyrightPreviewAttachment(null)}
      >
        <DialogContent
          className="bg-transparent border-none h-[95dvh] min-w-[90dvw] flex flex-col justify-center gap-y-2 outline-none"
          onInteractOutside={() => setCopyrightPreviewAttachment(null)}
          needCloseButton={false}
        >
          <div className="relative h-full w-full">
            {copyrightPreviewUrl && !copyrightPreviewFailed && (
              <img
                draggable={false}
                src={copyrightPreviewUrl}
                alt=""
                width={900}
                height={900}
                className="object-contain max-h-[90dvh] w-full absolute top-0 left-0"
                onError={() => setCopyrightPreviewFailed(true)}
              />
            )}
            {(!copyrightPreviewUrl || copyrightPreviewFailed) && (
              <div className="absolute inset-0 flex items-center justify-center px-8 text-center text-white">
                {vt(
                  "bo.mediaLibrary.watermark.previewUnavailable",
                  "La version filigranée de cette image n'est pas disponible.",
                )}
              </div>
            )}
            <div
              className="absolute right-[110px] top-[-20px] text-white cursor-pointer"
              onClick={() => setCopyrightPreviewAttachment(null)}
            >
              <Cross2Icon className="h-8 w-8" />
              <span className="sr-only">{t("formCancel")}</span>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {imageStudioHost}
    </Tabs>
  );
};

export default MediaLibrary;
